import api from 'api';
import CheckFilled from 'assets/CheckFilled';
import CloseIcon from 'assets/CloseIcon';
import DeleteButton from 'components/Buttons/DeleteButton';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import FormModal from 'components/Modal/FormModal';
import notificationUtils from 'components/Modal/NotificationsModal/utils';
import Select from 'components/Select';
import SelectContent from 'components/Select/components/SelectContent';
import SelectTrigger from 'components/Select/components/SelectTrigger';
import useModalHandler from 'hooks/useModalHandler';
import useSelect from 'hooks/useSelect';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import CollapsibleTableRow from 'scenes/Kyc/components/CollapsibleTableRow';
import { setNotifications } from 'store/actions/global.action';
import { selectNotifications } from 'store/selectors/global.selector';
import toastr from 'toastr';
import setClassSuffix from 'utilities/services/ClassManager';
import DateManager from 'utilities/services/DateManager';
import './styles.scss';
const {
  ICON_BASED_ON_TYPE,
  NOTIFICATION_STATUSES_TEXT,
  NOTIFICATION_STATUSES,
  replacePlaceholders,
  NOTIFICATION_DELETION_STATUSES,
  NOTIFICATION_TYPES,
} = notificationUtils;

const NotificationsTableRow = ({ rowInfo, onRemove, updateRowData }) => {
  const {
    type,
    status,
    dateCreated,
    dateUpdated,
    id,
    items: { notificationDeletionAction, activityLogs, headerItemForwardPath, headerItemName, title },
  } = rowInfo;
  const setSuffix = setClassSuffix('ickyc-table__column');
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const { isOpen: isConfirmModalOpen, open: openConfirmModal, close: closeConfirmModal } = useModalHandler();
  const history = useHistory();
  const dispatch = useDispatch();
  const { data: notifications, unreadNotificationsCount, ...rest } = useSelect(selectNotifications) || [];

  const deleteNotification = useCallback(async () => {
    try {
      await api.notifications.updateNotificationStatus({ id, notificationStatus: NOTIFICATION_STATUSES.DELETE });
      closeConfirmModal();
      onRemove('id', id);
      toastr.success('Successfully deleted notification');
    } catch (err) {
      console.error(err);
    }
  }, [id, onRemove, closeConfirmModal]);

  const changeStatus = async newStatus => {
    try {
      setIsStatusUpdating(true);
      await api.notifications.updateNotificationStatus({ id, notificationStatus: newStatus });
      updateRowData('id', id, { status: newStatus });
      // Update notifications
      const newNotifications = notifications.map(notification => {
        if (notification.id === id) {
          return { ...notification, status: newStatus };
        }
        return notification;
      });

      // Add notification manually, instead of update, because modal notifications don't include archived notifications
      if (status === NOTIFICATION_STATUSES.ARCHIVE) {
        newNotifications.unshift({
          id,
          type,
          status: NOTIFICATION_STATUSES.UNREAD,
          dateCreated,
          dateUpdated,
          items: {
            notificationDeletionAction,
            activityLogs,
            headerItemForwardPath,
            headerItemName,
            title,
          },
        });
      }
      const isStatusUnread = status =>
        status == NOTIFICATION_STATUSES.UNREAD || status == NOTIFICATION_STATUSES.UNREAD_MANUALLY;
      const isStatusRead = status => status == NOTIFICATION_STATUSES.READ || status == NOTIFICATION_STATUSES.ARCHIVE;

      const isNewStatusUnread = isStatusUnread(newStatus);
      const isOldStatusUnread = isStatusUnread(status);
      const isNewStatusRead = isStatusRead(newStatus);
      const isOldStatusRead = isStatusRead(status);

      // Check if both statuses are unread or both are read
      const shouldChangeCount = !(isNewStatusUnread && isOldStatusUnread) && !(isNewStatusRead && isOldStatusRead);

      dispatch(
        setNotifications({
          unreadNotificationsCount: unreadNotificationsCount + (shouldChangeCount ? (isNewStatusUnread ? 1 : -1) : 0),
          data: newNotifications,
          ...rest,
        }),
      );
      toastr.success('Successfully updated notification status');
    } catch (err) {
      console.error(err);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const notificationsStatusOptions = Object.entries(NOTIFICATION_STATUSES_TEXT).map(([key, value]) => {
    return {
      label: value,
      value: +key,
      id: key,
      disabled: +key === status,
    };
  });

  const navigate = useCallback(async () => {
    history.replace('/' + headerItemForwardPath);
  }, [headerItemForwardPath]);

  const handleDeletionAction = useCallback(async notificationDeletionAction => {
    try {
      if (type === NOTIFICATION_TYPES.CASE_DELETED) {
        await api.notifications.caseDeletionDecision(id, { notificationDeletionAction });
      } else {
        await api.notifications.entityDeletionDecision(id, { notificationDeletionAction });
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  const handleActionClick = useCallback(
    notificationDeletionAction => e => {
      e.stopPropagation();
      handleDeletionAction(notificationDeletionAction);
    },
    [],
  );

  return (
    <>
      <CollapsibleTableRow
        className="notifications-table-row ickyc-table"
        row={
          <>
            <td className={setSuffix('__type')}>
              {ICON_BASED_ON_TYPE[type]}
              <p className="notifications-table-row__title">{replacePlaceholders(title, { headerItemName })}</p>
            </td>

            <td className={setSuffix('__dateCreated')}>{DateManager.monDayYearLocal(dateCreated)}</td>
            <td className={setSuffix('__lastUpdate')}>{DateManager.getFullDateLocal(dateUpdated)}</td>
            <td className={setSuffix('__status')}>
              <Select
                options={notificationsStatusOptions}
                Content={<SelectContent />}
                Trigger={<SelectTrigger />}
                value={status}
                onChange={changeStatus}
                renderAsPortal
                onClick={e => e.stopPropagation()}
                loading={isStatusUpdating}
              />
            </td>
            <td className={setSuffix('__remove')}>
              <DeleteButton onClick={openConfirmModal} />
            </td>
          </>
        }
      >
        <div className="notifications-table-row__activity-logs" onClick={navigate}>
          {activityLogs.map(({ contentPlaceholder, id, ...rest }) => (
            <p key={id}>{replacePlaceholders(contentPlaceholder, { ...rest, headerItemName })}</p>
          ))}
          {(type === NOTIFICATION_TYPES.ENTITY_DELETED || type === NOTIFICATION_TYPES.CASE_DELETED) &&
            notificationDeletionAction === NOTIFICATION_DELETION_STATUSES.PENDING_APPROVAL && (
              <div className="notifications-table-row__deletion-actions">
                <PrimaryButton variant="red" onClick={handleActionClick(NOTIFICATION_DELETION_STATUSES.REJECTED)}>
                  <CloseIcon />
                  Reject Deletion
                </PrimaryButton>
                <PrimaryButton onClick={handleActionClick(NOTIFICATION_DELETION_STATUSES.DELETED)}>
                  <CheckFilled />
                  Approve Deletion
                </PrimaryButton>
              </div>
            )}
        </div>
      </CollapsibleTableRow>
      {isConfirmModalOpen && (
        <FormModal
          hideModal={closeConfirmModal}
          title="Delete Notification"
          className="ickyc-confirmation-moda ickyc-portal-management-confirm-modal"
          onSubmit={deleteNotification}
        >
          <span className="ickyc-confirmation-message">Are you sure you want to delete this notification?</span>
        </FormModal>
      )}
    </>
  );
};

export default NotificationsTableRow;
